import axios from "axios";
import { DSR_ACCESS_TOKEN } from "@/store/constants/global";
import { getToken } from "@/services/AuthService";

// PDF-APIのベースURL（環境変数から取得するか、デフォルト値を使用）
const PDF_API_BASE_URL =
  process.env.VUE_APP_PDF_API_URL || "http://localhost:8090";

// PDF-API用のaxiosインスタンスを作成
const pdfAxios = axios.create({
  baseURL: PDF_API_BASE_URL,
  timeout: 60000, // PDFの生成には時間がかかる可能性があるため、タイムアウトを長めに設定
});

/**
 * JWTのペイロード部（base64url）をデコードする
 * atob()はbase64url（-, _, パディング無し）を扱えないため変換してから渡す
 * @param {string} segment JWTのペイロード部
 * @returns {Object} デコードしたペイロード
 */
function decodeJwtPayload(segment) {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  return JSON.parse(atob(padded));
}

/**
 * 契約社員トークンが有効期限内かを確認する
 * 期限切れの古いトークンでAuth0セッションを上書きしないようにするためのチェック
 * @param {string} token JWTトークン
 * @returns {boolean} 有効期限内ならtrue（不正な形式・期限切れはfalse）
 */
function isContractorTokenValid(token) {
  try {
    const payload = decodeJwtPayload(token.split(".")[1]);
    return payload.exp > Math.floor(Date.now() / 1000);
  } catch (error) {
    console.error("契約社員トークンの検証エラー:", error);
    return false;
  }
}

// リクエストインターセプター
pdfAxios.interceptors.request.use(
  async function (config) {
    // Auth0の設定が存在するか確認
    const hasAuth0Config =
      process.env.VUE_APP_AUTH0_DOMAIN && process.env.VUE_APP_AUTH0_CLIENT_ID;

    let token;

    // 契約社員のトークンを最初にチェック（期限切れの場合はAuth0トークンにフォールバック）
    const contractorToken = localStorage.getItem("contractor_token");
    if (contractorToken && isContractorTokenValid(contractorToken)) {
      token = contractorToken;
    } else if (hasAuth0Config) {
      // Auth0が設定されている場合はlocalStorageからIDトークンを取得
      token = localStorage.getItem(DSR_ACCESS_TOKEN);

      // localStorageにトークンがない場合はAuth0から取得
      if (!token) {
        token = await getToken();
      }
    }

    // トークンがある場合は認証ヘッダーに追加
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    } else {
      console.warn(
        "認証トークンが見つかりません。PDF生成APIへのリクエストが失敗する可能性があります。"
      );
    }
    return config;
  },
  function (error) {
    console.error("PDF API request error:", error);
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
pdfAxios.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    console.error("PDF API response error:", error);
    return Promise.reject(error);
  }
);

/**
 * Calculate the number of lines needed for a text that wraps every 12 characters
 * @param {string} text - The text to calculate lines for
 * @returns {number} - Number of lines needed
 */
function calculateTextLines(text) {
  if (!text || typeof text !== 'string') {
    return 1;
  }
  const length = text.length;
  return Math.ceil(length / 12);
}

/**
 * Calculate the number of lines needed for qualification text that wraps every 2 characters
 * @param {string} text - The qualification text to calculate lines for
 * @returns {number} - Number of lines needed
 */
function calculateQualificationLines(text) {
  if (!text || typeof text !== 'string') {
    return 1;
  }
  const length = text.length;
  return Math.ceil(length / 2);
}

/**
 * Calculate the number of lines needed for primaryContractor text that wraps every 8 characters
 * @param {string} text - The primaryContractor text to calculate lines for
 * @returns {number} - Number of lines needed
 */
function calculatePrimaryContractorLines(text) {
  if (!text || typeof text !== 'string') {
    return 1;
  }
  const length = text.length;
  return Math.ceil(length / 8);
}

/**
 * Calculate the number of lines needed for subContractor text that wraps every 8 characters
 * @param {string} text - The subContractor text to calculate lines for
 * @returns {number} - Number of lines needed
 */
function calculateSubContractorLines(text) {
  if (!text || typeof text !== 'string') {
    return 1;
  }
  const length = text.length;
  return Math.ceil(length / 8);
}

function calculateConsumedRowLength(workRecords) {
  if (!workRecords || !Array.isArray(workRecords)) {
    console.log('[calculateConsumedRowLength] workRecords is null or not an array:', workRecords);
    return 0;
  }

  console.log('[calculateConsumedRowLength] Processing', workRecords.length, 'workRecords');
  
  let consumedRowLength = 0;
  
  for (let i = 0; i < workRecords.length; i++) {
    const workRecord = workRecords[i];
    const qualificatedResponsibleCount = Array.isArray(workRecord.qualificatedResponsible) 
      ? workRecord.qualificatedResponsible.length 
      : 0;
    
    let qualificatedResponsibleLineCount = 0;
    if (Array.isArray(workRecord.qualificatedResponsible)) {
      for (const responsible of workRecord.qualificatedResponsible) {
        if (responsible && responsible.qualification) {
          const lines = calculateQualificationLines(responsible.qualification);
          qualificatedResponsibleLineCount += lines;
        } else {
          qualificatedResponsibleLineCount += 1;
        }
      }
    }
    
    let safetyInstructionsLineCount = 0;
    if (Array.isArray(workRecord.safetyInstructions)) {
      for (const instruction of workRecord.safetyInstructions) {
        if (instruction && instruction.instruction) {
          const lines = calculateTextLines(instruction.instruction);
          safetyInstructionsLineCount += lines;
        } else {
          safetyInstructionsLineCount += 1;
        }
      }
    }
    
    const primaryContractorLineCount = calculatePrimaryContractorLines(
      workRecord.primaryConstractor ?? workRecord.primaryContractor ?? ''
    );
    const subContractorLineCount = calculateSubContractorLines(
      workRecord.subContractor ?? ''
    );
    
    const maxCount = Math.max(
      qualificatedResponsibleCount, 
      qualificatedResponsibleLineCount, 
      safetyInstructionsLineCount,
      primaryContractorLineCount,
      subContractorLineCount
    );
    const additionalRows = maxCount > 1 ? (maxCount - 1) : 0;
    
    console.log(`[calculateConsumedRowLength] WorkRecord ${i}:`, {
      qualificatedResponsibleCount,
      qualificatedResponsibleLineCount,
      safetyInstructionsLineCount,
      primaryContractorLineCount,
      subContractorLineCount,
      maxCount,
      additionalRows
    });
    
    consumedRowLength += additionalRows;
  }
  
  console.log('[calculateConsumedRowLength] Total consumedRowLength:', consumedRowLength);
  return consumedRowLength;
}

/**
 * ダッシュボードデータからPDFを生成する
 * @param {Object} diaryData - 日誌データ
 * @param {Object} siteMetaData - サイトのメタ情報（オプション）
 * @returns {Promise} - PDFのURLを含むPromise
 */
export function generatePdfFromDiary(diaryData, siteMetaData = null) {
  // 現在の日付と時刻を取得してファイル名に使用
  const now = new Date();
  const timestamp = now.toISOString().replace(/[:.]/g, "-");
  const fileName = `safety-diary-${timestamp}.pdf`;

  // JSONデータをS3に保存するためのキー
  const jsonKey = `temp/json/${timestamp}.json`;
  // 生成されるPDFのS3キー
  const pdfKey = `reports/${fileName}`;

  // テンプレートとCSSのパス
  const templatePath = "/app/templates/a3_landscape.html";
  const cssPath = "/app/static/styles.css";

  // リクエストデータ
  const requestData = {
    input_s3_object_key: jsonKey,
    output_s3_object_key: pdfKey,
    template_path: templatePath,
    css_path: cssPath,
  };

  // 2ステップのプロセス:
  // 1. まず日誌データをJSONとしてS3にアップロード
  // 2. 次にPDF生成APIを呼び出す

  // S3へのJSONアップロードはバックエンドAPIを通じて行う必要があるかもしれません
  // ここでは簡略化のため、PDF生成APIが直接JSONデータを受け取る形式に変更

  // サイトのメタ情報がある場合は、日誌データと統合
  const combinedData = {
    ...diaryData,
    siteMetaData: siteMetaData,
    totalConsumedRowLength: calculateConsumedRowLength(diaryData.workRecords)
  };

  return pdfAxios
    .post("/generate-pdf", {
      ...requestData,
      json_data: combinedData, // 統合したデータを送信
    })
    .then((response) => {
      console.log("PDF generated successfully:", response.data);

      // presigned_urlが返された場合はそれを使用
      if (response.data.presigned_url) {
        console.log("Using presigned URL:", response.data.presigned_url);
        response.data.pdf_url = response.data.presigned_url;
      } else {
        // 従来のoutput_locationを使用
        console.log("Using output location:", response.data.output_location);
        response.data.pdf_url = response.data.output_location;
      }

      return response.data;
    })
    .catch((error) => {
      console.error("Failed to generate PDF:", error);
      throw error;
    });
}

export default {
  generatePdfFromDiary,
};
